import { useState, useEffect } from "react";
import { storage, firestore } from "../../firebase-config";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, updateDoc, setDoc } from "firebase/firestore";

const UploadPlacementImage = () => {
  const [image, setImage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [allSlides, setAllSlides] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);

  // Initialize hardcoded slides in Firestore if they don't exist
  const initializeHardcodedSlides = async () => {
    try {
      const hardcodedSlides = [
        {
          id: "college-slide-1",
          type: "college",
          imagePath: "/images/collegeview.jpg",
          displayOrder: 1,
          isHardcoded: true
        },
        {
          id: "college-slide-2",
          type: "college",
          imagePath: "/images/polydrone.png",
          displayOrder: 999998,
          isHardcoded: true
        }
      ];

      for (const slide of hardcodedSlides) {
        const slideRef = doc(firestore, "169", slide.id);
        const slideDoc = await getDocs(collection(firestore, "169"));
        const exists = slideDoc.docs.some(d => d.id === slide.id);
        
        if (!exists) {
          await setDoc(slideRef, slide);
        }
      }
    } catch (error) {
      console.error("Error initializing hardcoded slides:", error);
    }
  };

  // Fetch all slides from Firestore
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        await initializeHardcodedSlides();
        
        const querySnapshot = await getDocs(collection(firestore, "169"));
        const slides = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort by displayOrder (ascending)
        const sortedSlides = slides.sort((a, b) => {
          const orderA = a.displayOrder ?? 999999;
          const orderB = b.displayOrder ?? 999999;
          return orderA - orderB;
        });
        
        setAllSlides(sortedSlides);
      } catch (error) {
        console.error("Error fetching slides:", error);
        setMessage("Error loading slides.");
      }
    };

    fetchSlides();
  }, []);

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setMessage("");
    }
  };

  // Upload image to Firebase Storage and save URL in Firestore
  const handleUpload = async () => {
    if (!image) {
      setMessage("Please select a valid image.");
      return;
    }

    const storageRef = ref(storage, `169/${image.name}`);
    const uploadTask = uploadBytesResumable(storageRef, image);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        setMessage("Upload failed.");
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Get the highest display order and add 1
          const maxOrder = allSlides.length > 0 
            ? Math.max(...allSlides.map(slide => slide.displayOrder ?? 0))
            : 0;
          
          const docRef = await addDoc(collection(firestore, "169"), {
            type: "placement",
            imageUrl: downloadURL,
            storagePath: `169/${image.name}`,
            uploadedAt: serverTimestamp(),
            displayOrder: maxOrder + 1,
            isHardcoded: false
          });

          setAllSlides((prevSlides) => [
            ...prevSlides,
            { 
              id: docRef.id,
              type: "placement",
              imageUrl: downloadURL, 
              storagePath: `169/${image.name}`,
              displayOrder: maxOrder + 1,
              isHardcoded: false
            },
          ]);

          setMessage("Upload successful!");
          setProgress(0);
          setImage(null);
        } catch (error) {
          console.error("Firestore error:", error);
          setMessage("Failed to save image in Firestore.");
        }
      }
    );
  };

  // Delete image from Storage and Firestore
  const handleDelete = async (id, storagePath, isHardcoded) => {
    if (isHardcoded) {
      setMessage("Cannot delete hardcoded college slides!");
      return;
    }

    try {
      const imageRef = ref(storage, storagePath);
      await deleteObject(imageRef);
      await deleteDoc(doc(firestore, "169", id));
      setAllSlides((prevSlides) => prevSlides.filter((slide) => slide.id !== id));
      setMessage("Image deleted successfully.");
    } catch (error) {
      console.error("Delete error:", error);
      setMessage("Failed to delete image.");
    }
  };

  // Update display order in Firestore
  const updateDisplayOrder = async (slideId, newOrder) => {
    try {
      await updateDoc(doc(firestore, "169", slideId), {
        displayOrder: newOrder
      });
    } catch (error) {
      console.error("Error updating order:", error);
      setMessage("Failed to update display order.");
    }
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === index) return;

    const newSlides = [...allSlides];
    const draggedSlide = newSlides[draggedItem];
    
    // Remove dragged item and insert at new position
    newSlides.splice(draggedItem, 1);
    newSlides.splice(index, 0, draggedSlide);
    
    setAllSlides(newSlides);
    setDraggedItem(index);
  };

  // Handle drag end - save new order to Firestore
  const handleDragEnd = async () => {
    if (draggedItem === null) return;

    try {
      // Update all display orders in batch
      const updatePromises = allSlides.map((slide, index) => 
        updateDisplayOrder(slide.id, index + 1)
      );
      
      await Promise.all(updatePromises);
      setMessage("Display order updated successfully!");
    } catch (error) {
      console.error("Error saving order:", error);
      setMessage("Failed to save display order.");
    }
    
    setDraggedItem(null);
  };

  // Move slide up
  const moveUp = async (index) => {
    if (index === 0) return;
    
    const newSlides = [...allSlides];
    [newSlides[index - 1], newSlides[index]] = [newSlides[index], newSlides[index - 1]];
    
    setAllSlides(newSlides);
    
    try {
      await updateDisplayOrder(newSlides[index - 1].id, index);
      await updateDisplayOrder(newSlides[index].id, index + 1);
      setMessage("Order updated!");
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  // Move slide down
  const moveDown = async (index) => {
    if (index === allSlides.length - 1) return;
    
    const newSlides = [...allSlides];
    [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];
    
    setAllSlides(newSlides);
    
    try {
      await updateDisplayOrder(newSlides[index].id, index + 1);
      await updateDisplayOrder(newSlides[index + 1].id, index + 2);
      setMessage("Order updated!");
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  // Get slide preview
  const getSlidePreview = (slide) => {
    if (slide.isHardcoded) {
      return slide.imagePath;
    }
    return slide.imageUrl;
  };

  // Get slide label
  const getSlideLabel = (slide) => {
    if (slide.isHardcoded && slide.id === "college-slide-1") {
      return "College View (Welcome Slide)";
    }
    if (slide.isHardcoded && slide.id === "college-slide-2") {
      return "College Drone View";
    }
    return `Placement Image`;
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Upload Placement Image</h2>
      <input type="file" accept="image/*" onChange={handleImageChange} />
      <button onClick={handleUpload} disabled={!image}>Upload</button>
      <progress value={progress} max="100"></progress>
      <p>{message}</p>

      <div style={{ marginTop: "20px" }}>
        <h3>Manage Slide Order (Drag to reorder or use arrow buttons)</h3>
        <p style={{ fontSize: "14px", color: "#666" }}>
          Arrange ALL slides including college images and placement images in the order you want them to appear
        </p>
        <p style={{ fontSize: "12px", color: "#999", fontStyle: "italic" }}>
          Note: College slides (hardcoded) cannot be deleted, only reordered
        </p>
      </div>

      {/* Display All Slides with Order Controls */}
      <div style={{ display: "flex", flexDirection: "column", marginTop: "20px", gap: "15px" }}>
        {allSlides.map((slide, index) => (
          <div
            key={slide.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              border: slide.isHardcoded ? "2px solid #2196F3" : "2px solid #ddd",
              padding: "15px",
              borderRadius: "8px",
              backgroundColor: draggedItem === index ? "#f0f0f0" : "white",
              cursor: "move",
              transition: "all 0.2s ease"
            }}
          >
            {/* Order Number */}
            <div style={{ 
              fontSize: "20px", 
              fontWeight: "bold", 
              minWidth: "40px",
              textAlign: "center",
              color: "#333"
            }}>
              #{index + 1}
            </div>

            {/* Image Preview */}
            <img
              src={getSlidePreview(slide)}
              alt={getSlideLabel(slide)}
              style={{ 
                width: "120px", 
                height: "80px", 
                objectFit: "cover", 
                borderRadius: "5px",
                border: "1px solid #ddd"
              }}
            />

            {/* Slide Label */}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                {getSlideLabel(slide)}
              </div>
              {slide.isHardcoded && (
                <div style={{ fontSize: "12px", color: "#2196F3", marginTop: "4px" }}>
                  🔒 College Slide (Cannot be deleted)
                </div>
              )}
            </div>

            {/* Order Control Buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                style={{
                  padding: "5px 15px",
                  background: index === 0 ? "#ccc" : "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: index === 0 ? "not-allowed" : "pointer",
                  fontSize: "12px"
                }}
              >
                ▲ Up
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === allSlides.length - 1}
                style={{
                  padding: "5px 15px",
                  background: index === allSlides.length - 1 ? "#ccc" : "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: index === allSlides.length - 1 ? "not-allowed" : "pointer",
                  fontSize: "12px"
                }}
              >
                ▼ Down
              </button>
            </div>

            {/* Drag Handle Indicator */}
            <div style={{ 
              fontSize: "12px", 
              color: "#999",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              <span>⋮⋮ Drag</span>
            </div>

            {/* Delete Button */}
            <button
              onClick={() => handleDelete(slide.id, slide.storagePath, slide.isHardcoded)}
              disabled={slide.isHardcoded}
              style={{
                padding: "8px 15px",
                background: slide.isHardcoded ? "#ccc" : "#f44336",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: slide.isHardcoded ? "not-allowed" : "pointer",
                fontWeight: "bold"
              }}
            >
              {slide.isHardcoded ? "🔒 Locked" : "Delete"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadPlacementImage;